import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skull, Pickaxe, Clock } from 'lucide-react';
import type { BitcoinBlock } from '@/hooks/useBitcoinBlocks';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@/hooks/useTheme';

interface TombstoneProps {
  block: BitcoinBlock;
  onClick: () => void;
  index: number;
}

export function Tombstone({ block, onClick, index }: TombstoneProps) {
  const { theme } = useTheme();
  const isNightmare = theme === 'nightmare';
  const timeAgo = formatDistanceToNow(new Date(block.timestamp * 1000), { addSuffix: true });

  return (
    <Card
      className={`tombstone-enter relative overflow-hidden cursor-pointer border-2 transition-all duration-300 group bg-card/80 backdrop-blur-sm ${
        isNightmare
          ? 'border-destructive/50 hover:border-destructive hover:glow-red hover:scale-105'
          : 'border-primary/30 hover:border-primary hover:glow-green'
      } ${isNightmare ? 'skull-float' : ''}`}
      onClick={onClick}
      style={{
        animationDelay: `${index * 100}ms`,
        animationDuration: isNightmare ? '2s' : '1.2s'
      }}
    >
      {/* Nightmare mode effects */}
      {isNightmare && (
        <>
          <div className="absolute inset-0 bg-gradient-to-b from-destructive/10 to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
          <div className="absolute top-2 right-2 text-2xl opacity-20">💀</div>
          <div className="absolute bottom-2 left-2 text-xl opacity-15">🪦</div>
        </>
      )}

      {/* Regular mode gradient */}
      {!isNightmare && (
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      )}

      <CardHeader className="pb-3 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skull className={`h-6 w-6 transition-colors ${
              isNightmare
                ? 'text-destructive animate-pulse group-hover:text-accent'
                : 'text-primary group-hover:text-accent'
            }`} />
            <span className={`font-gothic text-xl transition-colors ${
              isNightmare
                ? 'text-destructive group-hover:text-accent'
                : 'text-primary group-hover:text-accent'
            }`}>
              #{block.height.toLocaleString()}
            </span>
          </div>
          <div className={`flex items-center gap-1 text-xs ${
            isNightmare ? 'text-destructive/80' : 'text-muted-foreground'
          }`}>
            <Clock className="h-3 w-3" />
            <span>{timeAgo}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 relative">
        <div className="flex items-center justify-between text-sm">
          <span className={`flex items-center gap-1 ${
            isNightmare ? 'text-destructive/80' : 'text-muted-foreground'
          }`}>
            <Pickaxe className="h-3 w-3" />
            Transactions
          </span>
          <span className={`font-semibold transition-colors ${
            isNightmare
              ? 'text-destructive group-hover:text-destructive'
              : 'text-foreground group-hover:text-primary'
          }`}>
            {block.tx_count.toLocaleString()}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className={isNightmare ? 'text-destructive/80' : 'text-muted-foreground'}>
            Size
          </span>
          <span className={`font-semibold transition-colors ${
            isNightmare
              ? 'text-destructive group-hover:text-destructive'
              : 'text-foreground group-hover:text-primary'
          }`}>
            {(block.size / 1024 / 1024).toFixed(2)} MB
          </span>
        </div>

        <div className={`pt-2 border-t ${
          isNightmare ? 'border-destructive/30' : 'border-border/50'
        }`}>
          <div className={`text-xs font-mono truncate ${
            isNightmare ? 'text-destructive/60' : 'text-muted-foreground'
          }`}>
            {block.id}
          </div>
        </div>

        <div className={`absolute -bottom-2 -right-2 text-6xl transition-opacity pointer-events-none ${
          isNightmare ? 'opacity-10 group-hover:opacity-20' : 'opacity-5 group-hover:opacity-10'
        }`}>
          {isNightmare ? '💀' : '⚰️'}
        </div>
      </CardContent>
    </Card>
  );
}
